import * as React from 'react';
import { compose } from 'redux';
import cx from 'classnames';

// HOC
import { withUserWebView } from '@shopee-user/with-web-view';
import { withFirebasePerformance } from '@shopee-user-common/firebase-performance-utils';
import { withTracking } from '@shopee-user/tracking';

// Component
import { CrashComponent } from '@shopee-user/placeholder-ui';
import { BackgroundColorContainer } from '@shopee-user/background-color-container';
import { DesktopNavbar } from '@shopee-user/business-customization-ui';
import ErrorBoundary from '../shared/components/ErrorBoundary/ErrorBoundary';
import PageOffline from '@shopee-user/page-offline';

// Routes
import routes from '../routes';

// Hooks
import { useNavigationType, useLocation, useRoutes } from 'react-router-dom';
import {
  useDeferredInitialization,
  RenderBeaconContext,
} from '@shopee-user/deferred-initialization';
import { useMetricsReport } from '@shopee-user/real-time-metrics-report';
import { useResponsiveLayout } from '@shopee-user/layout-responsive';
import {
  useI18nInitialization,
  TranslationContext,
  I18nConfig,
} from '@shopee-user/i18n';

// Utils
import { getSafeAreaClassName } from '@shopee-user/with-web-view';
import { setUserLanguage } from '../utils/language';
import { Language } from '@shopee-user/global';
import {
  initSecuritySdk,
  initNativeDeviceFingerprint,
} from '@shopee-user/fetch-utils-v2';
import { isWebBridgeSupported } from '@shopee-user/platform';
import { parseQueryStringToObj } from '@shopee/url-util';

// Business customization
import { config } from '@shopee-user/business-customization-logic';

// Types
import { WithFirebasePerformanceProps } from '@shopee-user-common/firebase-performance-utils';
import { WithUserWebViewProps } from '@shopee-user/with-web-view';
import { TrackingSource, WithTrackingProps } from '@shopee-user/tracking';
import { Layout } from '@shopee-user/layout-responsive';

// For dead code elimination to remove it in live env
let Devtool: React.ComponentType | undefined;
const { disable_dev_tools } = parseQueryStringToObj(location.search);
if (__DISABLE_DEV_TOOLS__) {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  Devtool = undefined;
} else if (disable_dev_tools !== 'true') {
  // eslint-disable-next-line @typescript-eslint/no-var-requires
  Devtool = require('@shopee-user/devtool').default;
}

// Normalize CSS, colors, variables and set root theme
import styles from '@shopee-user/styles/account-business/app.scss';
// For business customized UI theme
import '@shopee-user/business-customization-logic/src/theme.css';

// Monitoring
import { platformTracker } from '../platform/monitoring/performance/tracker';
import NavigationDetection from '../platform/monitoring/performance/NavigationDetection';

type OwnProps = {
  i18nConfig: I18nConfig;
};

// We scroll to top if there is new route transition
// We check if action is `POP` then we will not scroll to top
// history.(go|goBack|goForward) and browser's forward/back will be `POP` action as well
function ScrollToTop() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  React.useLayoutEffect(() => {
    if (navigationType !== 'POP') {
      window.scrollTo(0, 0);
    }
  }, [pathname, navigationType]);

  return null;
}

function App({
  i18nConfig,
  initBITracker,
  initFirebasePerformanceTracker,
  recordFirebasePerformance,
  trackBIEvent: sendBITrackingViaWebView,
}: WithFirebasePerformanceProps &
  WithTrackingProps &
  WithUserWebViewProps &
  OwnProps): JSX.Element {
  const { layout } = useResponsiveLayout();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { setReady, isTimeout } = useDeferredInitialization(() => {
    initFirebasePerformanceTracker();
    initBITracker({
      sendBITrackingViaWebView,
      source:
        layout === Layout.Desktop ? TrackingSource.PC : TrackingSource.RWeb,
    });
  });
  const I18n = useI18nInitialization(i18nConfig);
  const [isOnline, setIsOnline] = React.useState(true);
  const elements = useRoutes(routes);
  const { reportMetric } = useMetricsReport();

  async function changeLanguage(language: Language) {
    if (setUserLanguage(language)) {
      return await I18n.changeLanguage(language);
    }
  }

  React.useEffect(() => {
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('offline', handleOffline);

    !isWebBridgeSupported() && initSecuritySdk(); // only need to init DFP in Web (non-App)
    initNativeDeviceFingerprint({ recordFirebasePerformance, reportMetric }); // retrieve native DFP

    platformTracker.trackAppRendered();

    return () => window.removeEventListener('offline', handleOffline);
  }, []);

  return (
    <ErrorBoundary fallback={({ error }) => <CrashComponent error={error} />}>
      <RenderBeaconContext.Provider value={{ setReady, isTimeout }}>
        <TranslationContext.Provider value={{ ...I18n, changeLanguage }}>
          <NavigationDetection />
          {!!Devtool && <Devtool />}
          <ScrollToTop />
          {isOnline ? (
            <div
              className={cx(
                styles.appContainer,
                // in the iPhone with `notch`, fullscreen mode will include status bar
                // therefore, we need to handle the safe area in layout
                getSafeAreaClassName()
              )}
            >
              <DesktopNavbar />
              <BackgroundColorContainer
                defaultBackgroundColor={config.Background.ColorDefault}
                backgroundColorMobile={config.Background.ColorMobile}
                backgroundColorDesktop={config.Background.ColorDesktop}
              >
                {elements}
              </BackgroundColorContainer>
            </div>
          ) : (
            <PageOffline />
          )}
        </TranslationContext.Provider>
      </RenderBeaconContext.Provider>
    </ErrorBoundary>
  );
}

export default compose(
  withTracking,
  withUserWebView({
    shouldShowFullScreen: true,
  }),
  withFirebasePerformance
)(App) as React.ComponentType<OwnProps>;
